/**
 * Helper functions for managing Branch Routing Groups
 */

let groupIdCounter = 0;

/**
 * Generate unique group ID
 */
export const generateGroupId = () => {
    groupIdCounter += 1;
    return `group-${Date.now()}-${groupIdCounter}`;
};

/**
 * Create a new routing group
 * @param {Array} platforms - Array of platform objects to include in group
 * @param {Number} priority - Priority value for the group
 * @returns {Object} New group object
 */
export const createGroup = (platforms = [], priority = 10) => {
    return {
        id: generateGroupId(),
        platforms: [...platforms],
        priority,
        type: 'group',
    };
};

/**
 * Add platform to existing group
 * @param {Array} groups - Current routing groups
 * @param {String} groupId - Target group ID
 * @param {Object} platform - Platform object to add
 * @returns {Array} Updated groups array
 */
export const addPlatformToGroup = (groups, groupId, platform) => {
    return groups.map((group) => {
        if (group.id === groupId) {
            return {
                ...group,
                platforms: [...group.platforms, platform],
            };
        }
        return group;
    });
};

/**
 * Remove platform from group
 * Auto-deletes group if less than 2 platforms remain
 * @param {Array} groups - Current routing groups
 * @param {String} groupId - Target group ID
 * @param {String} platformId - Platform ID to remove
 * @returns {Object} { updatedGroups, removedPlatforms }
 */
export const removePlatformFromGroup = (groups, groupId, platformId) => {
    const removedPlatforms = [];
    const updatedGroups = [];

    groups.forEach((group) => {
        if (group.id === groupId) {
            const remainingPlatforms = group.platforms.filter(
                (p) => p.platformID !== platformId,
            );

            // If less than 2 platforms remain, delete group and return platforms
            if (remainingPlatforms.length < 2) {
                removedPlatforms.push(...remainingPlatforms);
            } else {
                // Keep group with remaining platforms
                updatedGroups.push({
                    ...group,
                    platforms: remainingPlatforms,
                });
            }
        } else {
            updatedGroups.push(group);
        }
    });

    return {
        updatedGroups,
        removedPlatforms,
    };
};

/**
 * Remove all platforms from group (triggered by close button)
 * @param {Array} groups - Current routing groups
 * @param {String} groupId - Target group ID
 * @returns {Object} { updatedGroups, removedPlatforms }
 */
export const closeGroup = (groups, groupId) => {
    const removedPlatforms = [];
    const updatedGroups = groups.filter((group) => {
        if (group.id === groupId) {
            removedPlatforms.push(...group.platforms);
            return false; // Remove this group
        }
        return true;
    });

    return {
        updatedGroups,
        removedPlatforms,
    };
};

/**
 * Calculate final priorities for submission
 * Groups appear first, sorted by priority
 * Then standalone platforms
 * @param {Array} groups - Routing groups
 * @param {Array} standalonePlatforms - Platforms not in groups
 * @returns {Array} Flattened array with recalculated priorities
 */
export const calculateFinalPriorities = (groups, standalonePlatforms) => {
    const result = [];
    let currentPriority = 10;

    // Sort groups by priority
    const sortedGroups = [...groups].sort((a, b) => a.priority - b.priority);

    // Add platforms from groups
    sortedGroups.forEach((group) => {
        group.platforms.forEach((platform) => {
            result.push({
                ...platform,
                priority: currentPriority,
            });
            currentPriority += 1;
        });
    });

    // Add standalone platforms
    standalonePlatforms.forEach((platform) => {
        result.push({
            ...platform,
            priority: currentPriority,
        });
        currentPriority += 10;
    });

    return result;
};

/**
 * Calculate final priorities from unified list for submission
 * Processes items in array order (groups and platforms interleaved)
 * @param {Array} platformItems - Unified list of platforms and groups
 * @returns {Array} Flattened array with recalculated priorities
 */
export const calculateFinalPrioritiesFromUnified = (platformItems) => {
    const result = [];
    let currentPriority = 10;

    platformItems.forEach((item) => {
        if (item.type === 'group') {
            // All platforms in a group share the same priority (simultaneous ring)
            item.platforms.forEach((platform) => {
                result.push({
                    ...platform,
                    priority: currentPriority,
                });
            });
            // Only increment after the entire group
            currentPriority += 10;
        } else {
            // Standalone platform
            result.push({
                ...item,
                priority: currentPriority,
            });
            currentPriority += 10;
        }
    });

    return result;
};

/**
 * Reorder groups
 * @param {Array} groups - Current groups
 * @param {Number} startIndex - Source index
 * @param {Number} endIndex - Destination index
 * @returns {Array} Reordered groups with updated priorities
 */
export const reorderGroups = (groups, startIndex, endIndex) => {
    const result = Array.from(groups);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    // Update priorities
    return result.map((group, index) => ({
        ...group,
        priority: (index + 1) * 10,
    }));
};

/**
 * Find which group contains a platform
 * @param {Array} groups - All routing groups
 * @param {String} platformId - Platform ID to find
 * @returns {Object|null} Group object or null
 */
export const findGroupByPlatformId = (groups, platformId) => {
    return groups.find((group) =>
        group.platforms.some((p) => p.platformID === platformId),
    );
};

/**
 * Remove platform from all groups and standalone list
 * @param {Array} groups - Current groups
 * @param {Array} standalonePlatforms - Current standalone platforms
 * @param {String} platformId - Platform to remove
 * @returns {Object} { updatedGroups, updatedStandalone, removedPlatform }
 */
export const removePlatformFromAll = (
    groups,
    standalonePlatforms,
    platformId,
) => {
    let removedPlatform = null;
    let updatedGroups = groups;
    let updatedStandalone = standalonePlatforms;

    // Check standalone platforms
    const standaloneIndex = standalonePlatforms.findIndex(
        (p) => p.platformID === platformId,
    );
    if (standaloneIndex !== -1) {
        removedPlatform = standalonePlatforms[standaloneIndex];
        updatedStandalone = standalonePlatforms.filter(
            (p) => p.platformID !== platformId,
        );
        return { updatedGroups, updatedStandalone, removedPlatform };
    }

    // Check groups
    const parentGroup = findGroupByPlatformId(groups, platformId);
    if (parentGroup) {
        const result = removePlatformFromGroup(
            groups,
            parentGroup.id,
            platformId,
        );
        updatedGroups = result.updatedGroups;

        // Find the removed platform
        removedPlatform = parentGroup.platforms.find(
            (p) => p.platformID === platformId,
        );

        // Add any platforms that were ejected from deleted groups
        updatedStandalone = [
            ...standalonePlatforms,
            ...result.removedPlatforms.filter(
                (p) => p.platformID !== platformId,
            ),
        ];
    }

    return { updatedGroups, updatedStandalone, removedPlatform };
};

/**
 * Automatically group platforms by shared priority
 * Platforms with the same priority are grouped together (for simultaneous ringing)
 * Only runs on initial data load when simRingEnabled is true
 * @param {Array} platforms - Array of platform objects with type: 'platform'
 * @param {Boolean} simRingEnabled - Whether simultaneous ringing is enabled
 * @returns {Array} Unified list containing groups and standalone platforms
 */
export const autoGroupByPriority = (platforms, simRingEnabled) => {
    // If sim-ring is disabled or no platforms, return as-is
    if (!simRingEnabled || !platforms || platforms.length === 0) {
        return platforms;
    }

    // Group platforms by their priority value
    const priorityMap = platforms.reduce((acc, platform) => {
        const priority = platform.priority || 10; // Default to 10 if missing
        if (!acc[priority]) {
            acc[priority] = [];
        }
        acc[priority].push(platform);
        return acc;
    }, {});

    // Convert to unified list (groups for 2+ platforms, standalone for single)
    const result = [];

    // Sort by priority to maintain correct order
    Object.entries(priorityMap)
        .sort(([a], [b]) => Number(a) - Number(b))
        .forEach(([priority, platformsAtPriority]) => {
            if (platformsAtPriority.length >= 2) {
                // Create group for 2+ platforms with same priority
                result.push(createGroup(platformsAtPriority, Number(priority)));
            } else {
                // Single platform remains standalone
                result.push(platformsAtPriority[0]);
            }
        });

    return result;
};
